import { useContext } from 'react';
import { Box, alpha, Stack, lighten, Divider, IconButton, Tooltip, styled, useTheme } from '@mui/material';
import MenuTwoToneIcon from '@mui/icons-material/MenuTwoTone';
import { SidebarContext } from 'src/contexts/SidebarContext';
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone';
import { HeaderButtons } from './Buttons';
import HeaderMenu from './Menu';
import { DisplayCurrencySelector } from './DisplayCurrencySelector';
import { PrivacyModeToggle } from './PrivacyModeToggle';

interface HeaderWrapperProps {
  hasSidebar?: boolean;
}

const HeaderWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasSidebar'
})<HeaderWrapperProps>(
  ({ theme, hasSidebar }) => `
        height: ${theme.header.height};
        color: ${theme.header.textColor};
        padding: ${theme.spacing(0, 1)};
        right: 0;
        z-index: 6;
        background-color: ${alpha(theme.header.background, 0.95)};
        backdrop-filter: blur(3px);
        position: fixed;
        justify-content: space-between;
        width: 100%;
        
        @media (min-width: ${theme.breakpoints.values.sm}px) {
            padding: ${theme.spacing(0, 2)};
        }
        
        @media (min-width: ${theme.breakpoints.values.lg}px) {
            ${hasSidebar ? `
                left: ${theme.sidebar.width};
                width: auto;
            ` : ''}
        }
`
);

interface HeaderProps {
  hasSidebar?: boolean;
}

function Header({ hasSidebar = false }: HeaderProps) {
  const { sidebarToggle, toggleSidebar } = useContext(SidebarContext);
  const theme = useTheme();

  return (
    <HeaderWrapper
      display="flex"
      alignItems="center"
      hasSidebar={hasSidebar}
      sx={{
        boxShadow:
          theme.palette.mode === 'dark'
            ? `0 1px 0 ${alpha(
              lighten(theme.colors.primary.main, 0.7),
              0.15
            )}, 0px 2px 8px -3px rgba(0, 0, 0, 0.2), 0px 5px 22px -4px rgba(0, 0, 0, .1)`
            : `0px 2px 8px -3px ${alpha(
              theme.colors.alpha.black[100],
              0.2
            )}, 0px 5px 22px -4px ${alpha(
              theme.colors.alpha.black[100],
              0.1
            )}`
      }}
    >
      <Stack
        direction="row"
        divider={<Divider orientation="vertical" flexItem />}
        alignItems="center"
        spacing={2}
        sx={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}
      >
        <HeaderMenu />
      </Stack>
      <Box display="flex" alignItems="center" sx={{ flexShrink: 0 }}>
        <DisplayCurrencySelector />
        <PrivacyModeToggle />
        <HeaderButtons />
        {hasSidebar && (
          <Box
            component="span"
            sx={{
              ml: { xs: 0.5, sm: 2 },
              flexShrink: 0,
              display: { lg: 'none', xs: 'inline-block' }
            }}
          >
            <Tooltip arrow title="Toggle Menu">
              <IconButton
                color="primary"
                onClick={toggleSidebar}
                aria-label="Toggle Menu"
              >
                {!sidebarToggle ? (
                  <MenuTwoToneIcon fontSize="small" />
                ) : (
                  <CloseTwoToneIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </HeaderWrapper>
  );
}

export default Header;
